import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import cn from "@/v2-ui/utils/utils.cn"

/** What the live check found among the emails the form is about to send. */
export type CrmContactCounts = {
  /** In neither system — the ones a tick creates. */
  new: number
  /** Already HubSpot contacts — linked regardless of the tick. */
  contacts: number
  /** Already Backoffice users — nothing to create or link for them here. */
  users: number
}

export type CrmCreateContactCheckboxProps = {
  checked: boolean
  onCheckedChange(checked: boolean): void
  /** Undefined until the check has answered (nothing typed yet, or loading). */
  counts?: CrmContactCounts
  /** Single-person wording ("the contact") vs batch wording ("missing contacts"). */
  single?: boolean
  className?: string
}

/**
 * The one CRM decision on the create-user and invite forms, default on:
 * create a HubSpot contact for people who don't have one. Linking to an
 * existing contact by email always happens — this only governs creation,
 * and the helper line says exactly what a tick means for the emails typed
 * so far. Two fixed single lines: the block never changes height.
 */
function CrmCreateContactCheckbox(props: CrmCreateContactCheckboxProps) {
  const { checked, onCheckedChange, counts, single, className } = props

  const nothingToCreate = counts !== undefined && counts.new === 0
  const title = single
    ? "Also create the contact in HubSpot"
    : "Also create missing HubSpot contacts"

  let helper: string
  if (nothingToCreate) {
    if (counts.contacts > 0 && counts.users === 0) {
      helper = single
        ? "Already in HubSpot — the existing contact will be linked."
        : "Everyone here is already in HubSpot — existing contacts will be linked."
    } else if (counts.users > 0 && counts.contacts === 0) {
      helper = single
        ? "Already a Backoffice user — nothing to create."
        : counts.users === 1
          ? "Nothing to create — this person is already a Backoffice user."
          : "Nothing to create — these people are already Backoffice users."
    } else {
      helper =
        "Nothing to create — existing contacts will be linked; the rest are already Backoffice users."
    }
  } else if (checked) {
    helper =
      counts === undefined
        ? "Anyone without a HubSpot contact gets one, linked to their company, with you as owner."
        : single
          ? "No HubSpot contact yet — one will be created, linked to the company, with you as owner."
          : `${counts.new} of ${counts.new + counts.contacts + counts.users} ${counts.new === 1 ? "has" : "have"} no HubSpot contact — created, linked to the company, with you as owner.`
  } else {
    helper = single
      ? "Backoffice only — sync the user to HubSpot later from their panel."
      : "Backoffice only — people already in HubSpot are still linked; the rest can be synced later."
  }

  return (
    <Label
      className={cn(
        "flex min-h-[3.25rem] items-center gap-2 text-sm",
        nothingToCreate ? "cursor-default" : "cursor-pointer",
        className,
      )}
    >
      <Checkbox
        checked={nothingToCreate ? false : checked}
        disabled={nothingToCreate}
        onCheckedChange={(value) => {
          onCheckedChange(value === true)
        }}
      />
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{title}</span>
        <span className="truncate font-normal text-muted-foreground">
          {helper}
        </span>
      </span>
    </Label>
  )
}

export default CrmCreateContactCheckbox
